import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { Button, Icon, InputElement } from '../../../components';
import { mapServiceTypes } from '../../../utils';
import { SELF_SERVICE_MODES, VACUUM_INTERVALS } from './bulkPricing.constants';

import styles from './bulkPricingDrawer.module.scss';

const BulkPricingFieldPair = ({
  title,
  priceName,
  durationName,
  priceLabel,
  durationLabel,
  values,
  onChange,
  durationUnit,
}) => (
  <div className={styles.section}>
    <p className={styles.sectionTitle}>{title}</p>
    <div className={styles.fieldRow}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{priceLabel}</span>
        <InputElement
          name={priceName}
          type="number"
          onChange={onChange}
          value={values[priceName]}
          placeholder="0"
          min="0"
          step="0.01"
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{durationLabel}</span>
        <InputElement
          name={durationName}
          type="number"
          onChange={onChange}
          value={values[durationName]}
          placeholder="0"
          min="0"
          step="1"
        />
        {durationUnit && (
          <span className={styles.fieldHint}>{durationUnit}</span>
        )}
      </label>
    </div>
  </div>
);

const BulkPricingDrawer = ({
  serviceType,
  values,
  onChange,
  onClose,
  onUpdate,
  submitting,
}) => {
  const renderFields = () => {
    switch (serviceType) {
      case 21:
        return SELF_SERVICE_MODES.map(mode => (
          <BulkPricingFieldPair
            key={mode.id}
            title={mode.label}
            priceName={`${mode.id}Price`}
            durationName={`${mode.id}Duration`}
            priceLabel="Kaina su PVM, EUR"
            durationLabel="Trukmė, sekundės"
            values={values}
            onChange={onChange}
          />
        ));
      case 24:
        return VACUUM_INTERVALS.map(interval => (
          <div className={styles.section} key={interval.id}>
            <p className={styles.sectionTitle}>{interval.label}</p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Kaina su PVM, EUR</span>
              <InputElement
                name={`${interval.id}Price`}
                type="number"
                onChange={onChange}
                value={values[`${interval.id}Price`]}
                placeholder="0"
                min="0"
                step="0.01"
              />
            </label>
          </div>
        ));
      case 26:
        return (
          <>
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Vasarinis langų skystis</p>
              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    Kaina litro su PVM, EUR
                  </span>
                  <InputElement
                    name="summerPrice"
                    type="number"
                    onChange={onChange}
                    value={values.summerPrice}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Laikotarpis</span>
                  <select
                    name="summerMonth"
                    value={values.summerMonth}
                    onChange={onChange}
                    className={styles.monthSelect}
                  >
                    <option value="">0</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {
                          [
                            'Sausis',
                            'Vasaris',
                            'Kovas',
                            'Balandis',
                            'Gegužė',
                            'Birželis',
                            'Liepa',
                            'Rugpjūtis',
                            'Rugsėjis',
                            'Spalis',
                            'Lapkritis',
                            'Gruodis',
                          ][i]
                        }
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Žieminis langų skystis</p>
              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    Kaina litro su PVM, EUR
                  </span>
                  <InputElement
                    name="winterPrice"
                    type="number"
                    onChange={onChange}
                    value={values.winterPrice}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Laikotarpis</span>
                  <select
                    name="winterMonth"
                    value={values.winterMonth}
                    onChange={onChange}
                    className={styles.monthSelect}
                  >
                    <option value="">0</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {
                          [
                            'Sausis',
                            'Vasaris',
                            'Kovas',
                            'Balandis',
                            'Gegužė',
                            'Birželis',
                            'Liepa',
                            'Rugpjūtis',
                            'Rugsėjis',
                            'Spalis',
                            'Lapkritis',
                            'Gruodis',
                          ][i]
                        }
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </>
        );
      case 25:
        return (
          <div className={styles.section}>
            <p className={styles.sectionTitle}>Šluostės</p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Kaina VNT su PVM, EUR</span>
              <InputElement
                name="price"
                type="number"
                onChange={onChange}
                value={values.price}
                placeholder="0"
                min="0"
                step="0.01"
              />
            </label>
          </div>
        );
      case 27:
        return (
          <BulkPricingFieldPair
            title="Kilimėlių plovykla"
            priceName="price"
            durationName="duration"
            priceLabel="Kaina su PVM, EUR"
            durationLabel="Trukmė, minutės"
            values={values}
            onChange={onChange}
          />
        );
      case 29:
        return (
          <BulkPricingFieldPair
            title="Padangų juodinimas"
            priceName="price"
            durationName="duration"
            priceLabel="Kaina su PVM, EUR"
            durationLabel="Trukmė, sekundės"
            values={values}
            onChange={onChange}
          />
        );
      default:
        return null;
    }
  };

  return createPortal(
    <aside className={styles.drawer} role="complementary">
      <div className={styles.header}>
        <h2 className={styles.title}>{mapServiceTypes(serviceType)}</h2>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          <Icon name="close" classes={{ icon: styles.closeIcon }} />
        </button>
      </div>

      <div className={styles.body}>{renderFields()}</div>

      <div className={styles.footer}>
        <Button color="outline" type="button" onClick={onClose}>
          Uždaryti
        </Button>
        <Button
          color="primary"
          type="button"
          onClick={onUpdate}
          disabled={submitting}
        >
          {submitting ? 'Vykdoma...' : 'Atnaujinti'}
        </Button>
      </div>
    </aside>,
    document.body
  );
};

BulkPricingDrawer.propTypes = {
  serviceType: PropTypes.number.isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

BulkPricingDrawer.defaultProps = {
  submitting: false,
};

export default BulkPricingDrawer;
